import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { Reveal } from "../site/Reveal";
import { Rich } from "../site/Rich";
import { useT } from "@/i18n/useLocale";
import { FlujoVisual } from "./FlujoVisual";

export function FlujoFacturas() {
  const t = useT();
  const f = t.facturas.flujo;
  const [active, setActive] = useState(0);
  const stepRefs = useRef<Array<HTMLLIElement | null>>([]);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const nodes = stepRefs.current;
      const line = window.innerHeight * 0.5;
      let idx = 0;
      nodes.forEach((n, i) => {
        if (n && n.getBoundingClientRect().top <= line) idx = i;
      });
      setActive((prev) => (prev === idx ? prev : idx));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [f.steps.length]);

  return (
    <section
      id="flujo"
      className="relative scroll-mt-24 rounded-t-[40px] border-t border-white/5 bg-band-navy py-24 text-primary-foreground shadow-[0_-30px_60px_-20px_rgba(0,0,0,0.4)] lg:rounded-t-[56px] lg:py-32"
    >


      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <p className="mb-3 text-xs uppercase tracking-[0.18em] text-primary-foreground/60">{f.eyebrow}</p>
          <h2 className="font-display max-w-3xl text-4xl leading-[1.05] tracking-tight text-primary-foreground sm:text-5xl lg:text-6xl">
            {f.title}
          </h2>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-primary-foreground/75">{f.intro}</p>
        </Reveal>

        <div className="mt-16 grid gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
          <ol className="relative">
            {f.steps.map((s, i) => {
              const isActive = active === i;
              return (
                <li
                  key={s.title}
                  data-index={i}
                  ref={(el) => {
                    stepRefs.current[i] = el;
                  }}
                  className={`relative lg:flex lg:min-h-[70vh] lg:flex-col lg:justify-center ${i === f.steps.length - 1 ? "pb-0" : "pb-14 lg:pb-24"}`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex h-9 w-9 items-center justify-center rounded-full border text-sm font-semibold transition-colors ${
                        isActive
                          ? "border-accent bg-accent text-primary"
                          : "border-accent/45 bg-transparent text-accent"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="font-display text-xs uppercase tracking-[0.18em] text-accent">
                      {f.stepLabel} 0{i + 1}
                    </span>
                  </div>

                  <h3
                    className={`font-display mt-4 text-2xl leading-tight tracking-tight transition-colors lg:text-3xl ${
                      isActive ? "text-primary-foreground" : "text-primary-foreground/45"
                    }`}
                  >
                    {s.title}
                  </h3>
                  <Rich
                    text={s.body}
                    className="mt-3 block max-w-xl text-[15px] leading-relaxed text-primary-foreground/70"
                    strongClassName="font-semibold text-primary-foreground"
                  />

                  <ul className="mt-5 space-y-2.5">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex gap-3 text-[15px] leading-relaxed text-primary-foreground/70"
                      >
                        <Check className="mt-1 h-4 w-4 shrink-0 text-primary-glow" />
                        <Rich text={b} strongClassName="font-semibold text-primary-foreground" />
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 lg:hidden">
                    <FlujoVisual step={i} />
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="hidden lg:block">
            <div className="sticky top-28">
              <FlujoVisual step={active} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
